// The band as a control. Drag the ride along the day; it snaps to five minutes and reports every
// position so the numbers above it change as it moves. Arrow keys move it five minutes; Enter commits.
// Folded (the default): blocked time (work, kept evenings, after the latest finish) folds to a
// sliver each, so almost all the band answers the finger, and crossing a fold jumps the ride to
// the next free window. Either way the edges are elastic: push into blocked time and the ride
// leans in, names what's there, and springs back when you let go.
import { useRef, useState } from "preact/hooks";
import { haptic } from "../lib/haptic";
import { type Day, fillFor, lightStops, weatherStops, gradient, rainSpans, high, hhmm, foldAxis, foldStops, AXIS_START, AXIS_MIN } from "../lib/band";
import { clampStart, type Wall } from "../lib/day";

type Props = {
  day: Day;                        // rides[0] is the one that moves
  walls: Wall[];
  editable?: boolean;
  fold?: boolean;
  onMove?: (start: number, end: number) => void;    // every step while dragging
  onCommit?: (start: number, end: number) => void;  // on release, if it moved
  label?: string;
};

const short = (l: string) => (/^done by/i.test(l) ? l.replace(/^done by\s*/i, "After ") : l.split(/\s+/)[0]);
const pc = (x: number) => `${(x * 100).toFixed(2)}%`;

export function BandScrub({ day, walls, editable = true, fold = true, onMove, onCommit, label }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const ride = day.rides?.[0];
  const dur = ride ? ride.end - ride.start : 0;
  const ax = foldAxis(walls, fold && editable);
  const folded = ax.folds.length > 0;
  const [hit, setHit] = useState<Wall | null>(null);
  const [lean, setLean] = useState(0);      // elastic overshoot, as a share of the width
  const [jump, setJump] = useState(false);  // animate a jump across a fold
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ offset: number; from: number; last: number; hit: string | null } | null>(null);
  const fill = fillFor(day);
  const weather = fill === "weather" && day.hourly;
  const stops0 = weather ? weatherStops(day.hourly!) : lightStops(day.rise, day.set);
  const stops = folded ? foldStops(stops0, ax) : stops0;
  const L = (m: number) => pc(ax.P(m)), W = (a: number, b: number) => pc(ax.P(b) - ax.P(a));

  const shareAt = (clientX: number) => { const r = ref.current!.getBoundingClientRect(); return (clientX - r.left) / r.width; };
  /** Place the ride's start where the finger wants it; lean into a wall instead of stopping dead. */
  const place = (wantShare: number) => {
    const want = ax.M(wantShare);
    const { start, hit: w } = clampStart(want, dur, walls);
    const over = wantShare - ax.P(start);
    const leaning = w && Math.abs(over) > 0.006;
    setLean(leaning ? Math.sign(over) * 0.035 * (1 - Math.exp(-Math.abs(over) / 0.06)) : 0);
    const name = leaning ? w!.label : null;
    if (drag.current && name !== drag.current.hit) { drag.current.hit = name; if (name) haptic(); }
    setHit(leaning ? w : null);
    return start;
  };
  const report = (s: number) => {
    const d = drag.current; if (!d) return;
    if (s === d.last) return;
    if (Math.abs(ax.P(s) - ax.P(d.last)) > 0.08) { setJump(true); haptic(); setTimeout(() => setJump(false), 180); }
    d.last = s; onMove?.(s, s + dur);
  };

  const down = (e: PointerEvent) => {
    if (!editable || !ride) return;
    const at = shareAt(e.clientX), a = ax.P(ride.start), b = ax.P(ride.end);
    const inside = at >= a - 0.02 && at <= b + 0.02;
    drag.current = { offset: inside ? at - a : (b - a) / 2, from: ride.start, last: ride.start, hit: null };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
    if (!inside) report(place(at - (b - a) / 2));
  };
  const move = (e: PointerEvent) => { if (drag.current) report(place(shareAt(e.clientX) - drag.current.offset)); };
  const up = () => {
    const d = drag.current; if (!d) return;
    drag.current = null; setDragging(false); setLean(0); setTimeout(() => setHit(null), 900);
    if (d.last !== d.from) onCommit?.(d.last, d.last + dur);
  };
  const key = (e: KeyboardEvent) => {
    if (!editable || !ride) return;
    const step = e.key === "ArrowRight" ? 5 : e.key === "ArrowLeft" ? -5 : 0;
    if (step) { e.preventDefault(); const { start: s } = clampStart(ride.start + step, dur, walls); onMove?.(s, s + dur); }
    if (e.key === "Enter") onCommit?.(ride.start, ride.end);
  };

  // The free windows, named under a folded band.
  const windows: [number, number][] = [];
  if (folded) { let t = AXIS_START; for (const f of ax.folds) { if (f.start > t) windows.push([t, f.start]); t = Math.max(t, f.end); } if (t < AXIS_START + AXIS_MIN) windows.push([t, AXIS_START + AXIS_MIN]); }

  return (
    <div class={`scrub${editable ? " editable" : ""}${folded ? " folded" : ""}`}>
      <div class="scrub-track" ref={ref} onPointerDown={down as any} onPointerMove={move as any} onPointerUp={up} onPointerCancel={up}>
        <div class="band big" style={{ background: gradient(stops) }} aria-hidden="true">
          {weather && rainSpans(day.hourly!).map((r) => <span class="band-rain" style={{ left: L(r.start), width: W(r.start, r.end) }} />)}
          {weather && <><span class="band-tick" style={{ left: L(day.rise) }} /><span class="band-tick" style={{ left: L(day.set) }} /></>}
        </div>
        {folded ? ax.folds.map((f, i) => {
          const w = walls.find((x) => x.start < f.end && x.end > f.start);
          const on = hit && hit.start < f.end && hit.end > f.start;
          return (
            <span class={`scrub-fold${on ? " hit" : ""}${f.at < 0.12 ? " first" : f.at + f.w > 0.88 ? " last" : ""}`} data-i={i} style={{ left: pc(f.at), width: pc(f.w) }} aria-hidden="true">
              <span class="scrub-fold-label">{on ? `${hit!.label}, ${hhmm(f.start)} to ${hhmm(f.end)}` : short(w?.label ?? "")}</span>
            </span>
          );
        }) : walls.map((w) => (
          <span class={`scrub-wall${hit?.label === w.label ? " hit" : ""}`} style={{ left: L(w.start), width: W(w.start, w.end) }} aria-hidden="true">
            <span class={`scrub-wall-label${w.end >= AXIS_START + AXIS_MIN ? " end" : ""}`}>{w.label}</span>
          </span>
        ))}
        {!folded && (day.kept ?? []).map((k) => <span class="band-kept big" style={{ left: L(k.start), width: W(k.start, k.end) }} />)}
        {ride && (
          <span class={`scrub-ride${ride.done ? " done" : ""}${jump ? " jump" : ""}${dragging ? " dragging" : ""}`}
            style={{ left: `calc(${L(ride.start)} + ${(lean * 100).toFixed(2)}%)`, width: W(ride.start, ride.end) }}
            role={editable ? "slider" : undefined} tabIndex={editable ? 0 : -1} onKeyDown={key as any}
            aria-label={label ?? "Ride time"} aria-valuemin={AXIS_START} aria-valuemax={AXIS_START + AXIS_MIN - dur} aria-valuenow={ride.start} aria-valuetext={`${hhmm(ride.start)} to ${hhmm(ride.end)}`}>
            {editable && <span class="grip" aria-hidden="true" />}
          </span>
        )}
      </div>
      {folded && (
        <div class="fold-edges" aria-hidden="true">
          {windows.map(([a, b]) => <span class="label" style={{ left: L(a), width: W(a, b) }}>{hhmm(a)} to {hhmm(b)}</span>)}
        </div>
      )}
      <div class="band-ticks">
        {!folded && <span class="label">05:00</span>}
        {weather ? (
          <>
            <span class="label amber">High {Math.round(high(day.hourly!).feels)}° at {hhmm(high(day.hourly!).at)}</span>
            <span class="label">{rainSpans(day.hourly!).length ? `Rain ${rainSpans(day.hourly!).map((s) => `${hhmm(s.start).slice(0, 2)} to ${hhmm(s.end).slice(0, 2)}`).join(", ")}` : "Dry"}</span>
          </>
        ) : (
          <>
            <span class="label amber">Sunrise {hhmm(day.rise)}</span>
            <span class="label dusk">Sunset {hhmm(day.set)}</span>
          </>
        )}
        {!folded && <span class="label">22:00</span>}
      </div>
    </div>
  );
}
